import React, { useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BottomNav } from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import { Gem, Star, CheckCircle, Calendar, Circle, Plus, Check, Shield, Camera, X, QrCode, Upload, FileText, File, Info, Award, FileCheck, Clock, TrendingUp } from 'lucide-react';
interface ActivityPageProps {
  onNavigate: (page: string) => void;
  actionIdToOpen?: string | null;
  onConsumedActionIdToOpen?: () => void;
}
export function ActivityPage({
  onNavigate,
  actionIdToOpen,
  onConsumedActionIdToOpen
}: ActivityPageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'planned' | 'unplanned' | 'recent'>('planned');
  const [showCertificationModal, setShowCertificationModal] = useState(false);
  const [showSelfCertificationModal, setShowSelfCertificationModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<any | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isCreditsClosing, setIsCreditsClosing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFilePreview, setUploadedFilePreview] = useState<string | null>(null);
  const [certificationNotes, setCertificationNotes] = useState('');
  const {
    wallet,
    actions,
    uploadProof,
    showToast
  } = useApp();
  // Auto-open modal if actionIdToOpen is provided
  useEffect(() => {
    if (actionIdToOpen) {
      const action = actions.find(a => a.id === actionIdToOpen);
      if (action) {
        setSelectedAction(action);
        if (action.type === 'diamond') {
          setShowCertificationModal(true);
        } else {
          setShowSelfCertificationModal(true);
        }
      }
      // Consume the actionIdToOpen immediately to prevent reopen
      if (onConsumedActionIdToOpen) {
        onConsumedActionIdToOpen();
      }
    }
  }, [actionIdToOpen, actions, onConsumedActionIdToOpen]);
  useEffect(() => {
    const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
    const handleScroll = () => {
      if (scrollArea) {
        setIsScrolled(scrollArea.scrollTop > 20);
      }
    };
    if (scrollArea) {
      scrollArea.addEventListener('scroll', handleScroll);
      return () => scrollArea.removeEventListener('scroll', handleScroll);
    }
  }, []);
  const handleVerifyTask = (action: any) => {
    setSelectedAction(action);
    if (action.type === 'diamond') {
      setShowCertificationModal(true);
    } else {
      setShowSelfCertificationModal(true);
    }
  };
  const handleCloseCertificationModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowCertificationModal(false);
      setShowSelfCertificationModal(false);
      setSelectedAction(null);
      setIsClosing(false);
      setUploadedFile(null);
      setUploadedFilePreview(null);
      setCertificationNotes('');
    }, 300);
  };
  const handleCloseCreditsModal = () => {
    setIsCreditsClosing(true);
    setTimeout(() => {
      setShowCreditsModal(false);
      setIsCreditsClosing(false);
    }, 300);
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setUploadedFilePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setUploadedFilePreview(null);
      }
    }
  };
  const handleSubmitCertification = () => {
    if (!selectedAction || !uploadedFile) return;
    // Upload proof
    uploadProof(selectedAction.id, uploadedFile);
    // Show success toast
    showToast({
      variant: 'success',
      message: 'Prova caricata!'
    });
    // Close modal and navigate back to home
    handleCloseCertificationModal();
    onNavigate('home');
  };
  // Filter actions by status and scheduled date
  const plannedActions = actions.filter(a => a.status === 'planned' && a.scheduledAt);
  // Mock unplanned actions (in real app would come from backend)
  const unplannedActions = [{
    id: 'unplanned-1',
    emoji: '💡',
    title: 'Installare lampadine LED',
    points: 20,
    type: 'gold' as const,
    status: 'planned' as const
  }, {
    id: 'unplanned-2',
    emoji: '🚿',
    title: 'Fare docce brevi',
    points: 15,
    type: 'gold' as const,
    status: 'planned' as const
  }];
  // Mock recent actions matching HomePage
  const recentActions = [{
    id: 'recent-1',
    emoji: '🚲',
    title: 'In bici al lavoro',
    completedAt: 'Oggi, 8:30',
    points: 50,
    type: 'diamond' as const,
    status: 'completed' as const
  }, {
    id: 'recent-2',
    emoji: '♻️',
    title: 'Riciclato il vetro',
    completedAt: 'Ieri, 14:20',
    points: 10,
    type: 'gold' as const,
    status: 'completed' as const
  }];
  const todayPlanned = plannedActions.filter(a => a.scheduledAt?.startsWith('Oggi'));
  const upcomingPlanned = plannedActions.filter(a => !a.scheduledAt?.startsWith('Oggi'));
  // Calculate weekly impact (mock data - in real app would come from backend)
  const weeklyImpact = 85;
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-194">
      {/* Fixed Header */}
      <header className={`fixed top-0 left-0 right-0 px-6 py-4 flex justify-between items-center z-30 transition-all duration-300 ${isScrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'}`} data-id="element-195">
        <div className="flex items-center" data-id="element-196">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className={`h-8 w-auto transition-all duration-300 ${isScrolled ? '' : 'brightness-0 invert'}`} data-id="element-197" />
        </div>
        <div className="flex items-center gap-3" data-id="element-198">
          <button onClick={() => setShowCreditsModal(true)} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-green-600' : 'text-white'}`} data-id="element-199">
            <Gem className="h-5 w-5" strokeWidth={2} data-id="element-200" />
            <span className="text-sm font-normal" data-id="element-201">{wallet.diamonds}</span>
          </button>
          <button onClick={() => setShowCreditsModal(true)} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-gray-600' : 'text-white'}`} data-id="element-202">
            <Star className="h-5 w-5" strokeWidth={2} data-id="element-203" />
            <span className="text-sm font-normal" data-id="element-204">{wallet.stars}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <ScrollArea className="flex-1" data-id="element-205">
        {/* Hero Background with Weekly Impact */}
        <div className="bg-green-600 w-full relative pt-20 pb-10" data-id="element-206">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
          backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/wsCbUWp4TF3ni7DwEfb9qP/hand-drawn-turtle-shell-pattern_23-2150598661.jpg)'
        }} data-id="element-207" />
          <div className="relative z-10 px-6 max-w-md mx-auto" data-id="element-208">
            <div className="mb-5" data-id="element-209">
              <h2 className="font-normal text-2xl text-white mb-1" data-id="element-210">
                Le tue attività
              </h2>
              <p className="text-sm text-white/80" data-id="element-211">
                {new Date().toLocaleDateString('it-IT', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
              </p>
            </div>

            {/* Weekly Impact Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20" data-id="element-212">
              <div className="flex items-center justify-between mb-3" data-id="element-213">
                <div className="flex items-center gap-2" data-id="element-214">
                  <TrendingUp className="h-5 w-5 text-white" strokeWidth={2} data-id="element-215" />
                  <span className="text-sm text-white/90" data-id="element-216">
                    Impatto settimanale
                  </span>
                </div>
                <span className="text-2xl font-normal text-white" data-id="element-217">
                  {weeklyImpact}%
                </span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2" data-id="element-218">
                <div className="bg-white h-full rounded-full transition-all duration-500" style={{
                width: `${weeklyImpact}%`
              }} data-id="element-219" />
              </div>
              <p className="text-xs text-white/70 mt-2" data-id="element-220">
                {plannedActions.length} azioni pianificate •{' '}
                {recentActions.length} completate
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 pb-28 max-w-md mx-auto relative" data-id="element-221">
          {/* Tab Switcher - More spacing from hero */}
          <div className="relative -mt-3 mb-6" data-id="element-222">
            <div className="bg-white p-1 rounded-full border border-gray-200 flex overflow-x-auto scrollbar-hide" data-id="element-223">
              <button onClick={() => setActiveTab('planned')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 whitespace-nowrap ${activeTab === 'planned' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-224">
                Pianificate
              </button>
              <button onClick={() => setActiveTab('unplanned')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 whitespace-nowrap ${activeTab === 'unplanned' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-225">
                Da Pianificare
              </button>
              <button onClick={() => setActiveTab('recent')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 whitespace-nowrap ${activeTab === 'recent' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-226">
                Recenti
              </button>
            </div>
          </div>

          {/* Content based on active tab */}
          <div className="space-y-6" data-id="element-227">
            {activeTab === 'planned' && <>
                {todayPlanned.length > 0 && <div data-id="element-228">
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3 px-1" data-id="element-229">
                      Oggi
                    </h3>
                    <div className="space-y-3" data-id="element-230">
                      {todayPlanned.map(action => <div key={action.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-green-500 transition-colors bg-white" data-id="element-231">
                          <div className="text-2xl" data-id="element-232">{action.emoji}</div>
                          <div className="flex-1" data-id="element-233">
                            <p className="font-normal text-sm text-gray-900" data-id="element-234">
                              {action.title}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-235">
                              📅 {action.scheduledAt}
                            </p>
                          </div>
                          <button onClick={() => handleVerifyTask(action)} className="px-3 py-1.5 rounded-full text-xs font-normal transition-all bg-white border-2 border-green-600 text-green-600 hover:bg-green-50" data-id="element-236">
                            <span className="flex items-center gap-1" data-id="element-237">
                              {action.type === 'diamond' ? 'Certifica' : 'Verifica'}
                              {action.type === 'diamond' ? <Gem className="h-3 w-3" strokeWidth={2} data-id="element-238" /> : <Star className="h-3 w-3" strokeWidth={2} data-id="element-239" />}
                              <span data-id="element-240">+{action.points}</span>
                            </span>
                          </button>
                        </div>)}
                    </div>
                  </div>}

                {upcomingPlanned.length > 0 && <div data-id="element-241">
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3 px-1" data-id="element-242">
                      Prossime
                    </h3>
                    <div className="space-y-3" data-id="element-243">
                      {upcomingPlanned.map(action => <div key={action.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-green-500 transition-colors bg-white" data-id="element-244">
                          <div className="text-2xl" data-id="element-245">{action.emoji}</div>
                          <div className="flex-1" data-id="element-246">
                            <p className="font-normal text-sm text-gray-900" data-id="element-247">
                              {action.title}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-248">
                              📅 {action.scheduledAt}
                            </p>
                          </div>
                          <button onClick={() => handleVerifyTask(action)} className="px-3 py-1.5 rounded-full text-xs font-normal transition-all bg-white border-2 border-green-600 text-green-600 hover:bg-green-50" data-id="element-249">
                            <span className="flex items-center gap-1" data-id="element-250">
                              {action.type === 'diamond' ? 'Certifica' : 'Verifica'}
                              {action.type === 'diamond' ? <Gem className="h-3 w-3" strokeWidth={2} data-id="element-251" /> : <Star className="h-3 w-3" strokeWidth={2} data-id="element-252" />}
                              <span data-id="element-253">+{action.points}</span>
                            </span>
                          </button>
                        </div>)}
                    </div>
                  </div>}

                {plannedActions.length === 0 && <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200" data-id="element-254">
                    <div className="text-5xl mb-4" data-id="element-255">📅</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-256">
                      Nessuna attività pianificata
                    </h3>
                    <p className="text-sm text-gray-500 mb-6" data-id="element-257">
                      Pianifica le tue azioni per iniziare
                    </p>
                    <button onClick={() => onNavigate('impact')} className="px-6 py-2.5 rounded-xl text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-colors" data-id="element-258">
                      Esplora azioni
                    </button>
                  </div>}
              </>}

            {activeTab === 'unplanned' && <>
                {unplannedActions.length > 0 ? <div className="space-y-3" data-id="element-259">
                    {unplannedActions.map(action => <div key={action.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-green-500 transition-colors bg-white" data-id="element-260">
                        <div className="text-2xl" data-id="element-261">{action.emoji}</div>
                        <div className="flex-1" data-id="element-262">
                          <p className="font-normal text-sm text-gray-900" data-id="element-263">
                            {action.title}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-264">
                            Nessuna data impostata
                          </p>
                        </div>
                        <button onClick={() => handleVerifyTask(action)} className="px-3 py-1.5 rounded-full text-xs font-normal transition-all bg-white border-2 border-green-600 text-green-600 hover:bg-green-50" data-id="element-265">
                          <span className="flex items-center gap-1" data-id="element-266">
                            {action.type === 'diamond' ? 'Certifica' : 'Verifica'}
                            {action.type === 'diamond' ? <Gem className="h-3 w-3" strokeWidth={2} data-id="element-267" /> : <Star className="h-3 w-3" strokeWidth={2} data-id="element-268" />}
                            <span data-id="element-269">+{action.points}</span>
                          </span>
                        </button>
                      </div>)}
                  </div> : <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200" data-id="element-270">
                    <div className="text-5xl mb-4" data-id="element-271">✅</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-272">
                      Tutte le azioni sono pianificate
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-273">
                      Ottimo lavoro! Continua così
                    </p>
                  </div>}
              </>}

            {activeTab === 'recent' && <>
                {recentActions.length > 0 ? <div className="space-y-3" data-id="element-274">
                    {recentActions.map(action => <div key={action.id} className="bg-white rounded-xl p-4 border border-gray-100" data-id="element-275">
                        <div className="flex items-start gap-3 mb-3" data-id="element-276">
                          <div className="text-2xl" data-id="element-277">{action.emoji}</div>
                          <div className="flex-1" data-id="element-278">
                            <p className="font-normal text-sm text-gray-900 mb-1" data-id="element-279">
                              {action.title}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-280">
                              {action.completedAt}
                            </p>
                          </div>
                          <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full" data-id="element-281">
                            <Check className="h-3 w-3" data-id="element-282" />
                            Verificato
                          </span>
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-283">
                          <div className="flex items-center gap-1 text-sm font-normal text-gray-600" data-id="element-284">
                            {action.type === 'diamond' ? <Gem className="h-4 w-4 text-green-600" strokeWidth={2} data-id="element-285" /> : <Star className="h-4 w-4 text-gray-400" strokeWidth={2} data-id="element-286" />}
                            <span data-id="element-287">+{action.points}</span>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200" data-id="element-288">
                    <div className="text-5xl mb-4" data-id="element-289">🎯</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-290">
                      Nessuna attività completata
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-291">
                      Completa la tua prima azione per vederla qui
                    </p>
                  </div>}
              </>}
          </div>
        </div>
      </ScrollArea>

      <BottomNav activePage="activity" onNavigate={onNavigate} data-id="element-292" />

      {/* Credits Modal */}
      {showCreditsModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseCreditsModal} data-id="element-293" />

          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isCreditsClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-294">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-295">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-296" />
              <div className="flex items-center justify-between" data-id="element-297">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-298">
                  Come funzionano i crediti
                </h3>
                <button onClick={handleCloseCreditsModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-299">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-300" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-301">
              <div className="space-y-6" data-id="element-302">
                {/* Verification Levels */}
                <div data-id="element-303">
                  <h4 className="font-normal text-base text-gray-900 mb-4" data-id="element-304">
                    Livelli di verifica delle azioni
                  </h4>
                  <div className="space-y-3" data-id="element-305">
                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-306">
                      <div className="flex items-start gap-3" data-id="element-307">
                        <FileCheck className="h-5 w-5 text-gray-400 flex-shrink-0" strokeWidth={2} data-id="element-308" />
                        <div className="flex-1" data-id="element-309">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-310">
                            Non verificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-311">
                            Azioni tracciate per statistiche e profilazione. Non
                            danno crediti.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-312">
                      <div className="flex items-start gap-3" data-id="element-313">
                        <Award className="h-5 w-5 text-yellow-500 flex-shrink-0" strokeWidth={2} data-id="element-314" />
                        <div className="flex-1" data-id="element-315">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-316">
                            Verificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-317">
                            Verifica tramite auto-certificazione. Danno crediti
                            Stelle.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-318">
                      <div className="flex items-start gap-3" data-id="element-319">
                        <Shield className="h-5 w-5 text-green-600 flex-shrink-0" strokeWidth={2} data-id="element-320" />
                        <div className="flex-1" data-id="element-321">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-322">
                            Certificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-323">
                            Certificate da Greenback o partner ufficiali.
                            Registrate su Blockchain. Danno crediti Diamond.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Credit Types */}
                <div data-id="element-324">
                  <h4 className="font-normal text-base text-gray-900 mb-4" data-id="element-325">
                    Cosa puoi fare con i crediti
                  </h4>
                  <div className="space-y-3" data-id="element-326">
                    <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-327">
                      <div className="flex items-start gap-3 mb-3" data-id="element-328">
                        <Star className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={2} fill="currentColor" data-id="element-329" />
                        <div className="flex-1" data-id="element-330">
                          <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-331">
                            Crediti Stelle
                          </h4>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-332">
                            Valore non monetario
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2" data-id="element-333">
                        <p className="text-xs text-gray-700" data-id="element-334">
                          <span className="font-medium" data-id="element-335">✓</span> Partecipazione
                          a eventi esclusivi
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-336">
                          <span className="font-medium" data-id="element-337">✓</span> Sblocco di
                          feature premium nell'app
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-338">
                          <span className="font-medium" data-id="element-339">✓</span> Accesso a
                          contenuti riservati
                        </p>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-340">
                      <div className="flex items-start gap-3 mb-3" data-id="element-341">
                        <Gem className="h-6 w-6 text-green-600 flex-shrink-0" strokeWidth={2} data-id="element-342" />
                        <div className="flex-1" data-id="element-343">
                          <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-344">
                            Crediti Diamond
                          </h4>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-345">
                            Valore monetario e ufficiale
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2" data-id="element-346">
                        <p className="text-xs text-gray-700" data-id="element-347">
                          <span className="font-medium" data-id="element-348">✓</span> Convertibili in
                          buoni acquisto
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-349">
                          <span className="font-medium" data-id="element-350">✓</span> Convertibili in
                          denaro
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-351">
                          <span className="font-medium" data-id="element-352">✓</span> Certificazione
                          su Blockchain
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-353">
                          <span className="font-medium" data-id="element-354">✓</span> Riconoscimento
                          ufficiale e formale
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-355">
                  <div className="flex items-start gap-3" data-id="element-356">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} data-id="element-357" />
                    <div data-id="element-358">
                      <p className="text-xs text-blue-900 leading-relaxed" data-id="element-359">
                        <span className="font-medium" data-id="element-360">Nota:</span> Le azioni
                        certificate richiedono una verifica ufficiale da parte
                        di Greenback o dei nostri partner autorizzati. Una volta
                        certificate, vengono registrate permanentemente su
                        Blockchain per garantire trasparenza e autenticità.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Certification Modal (Diamond Actions) */}
      {showCertificationModal && selectedAction && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseCertificationModal} data-id="element-361" />

          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-362">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-363">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-364" />
              <div className="flex items-center justify-between" data-id="element-365">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-366">
                  Certificazione ufficiale
                </h3>
                <button onClick={handleCloseCertificationModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-367">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-368" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-369">
              <div className="space-y-6" data-id="element-370">
                {/* Task Info */}
                <div className="bg-green-50 rounded-xl p-4 border border-green-100" data-id="element-371">
                  <div className="flex items-center gap-3 mb-2" data-id="element-372">
                    <div className="text-3xl" data-id="element-373">{selectedAction.emoji}</div>
                    <div className="flex-1" data-id="element-374">
                      <h4 className="font-normal text-base text-gray-900" data-id="element-375">
                        {selectedAction.title}
                      </h4>
                      <div className="flex items-center gap-1 text-xs text-green-600 mt-1" data-id="element-376">
                        <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-377" />
                        <span data-id="element-378">+{selectedAction.points} Punti Diamond</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* File Upload Section */}
                <div data-id="element-379">
                  <label className="block text-sm font-normal text-gray-900 mb-3" data-id="element-380">
                    Carica documento o foto
                  </label>
                  {uploadedFile ? <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-381">
                      <div className="flex items-start gap-3" data-id="element-382">
                        {uploadedFilePreview ? <img src={uploadedFilePreview} alt="Preview" className="w-20 h-20 object-cover rounded-lg" data-id="element-383" /> : <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-384">
                            <File className="h-8 w-8 text-gray-400" data-id="element-385" />
                          </div>}
                        <div className="flex-1 min-w-0" data-id="element-386">
                          <p className="text-sm font-normal text-gray-900 truncate" data-id="element-387">
                            {uploadedFile.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-388">
                            {(uploadedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <button onClick={() => {
                    setUploadedFile(null);
                    setUploadedFilePreview(null);
                  }} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-389">
                          <X className="h-5 w-5" data-id="element-390" />
                        </button>
                      </div>
                    </div> : <label className="w-full bg-gray-50 rounded-xl p-8 border-2 border-dashed border-gray-300 hover:border-green-500 transition-all cursor-pointer flex flex-col items-center justify-center" data-id="element-391">
                      <Upload className="h-12 w-12 text-gray-400 mb-3" data-id="element-392" />
                      <p className="text-sm font-normal text-gray-600 mb-1" data-id="element-393">
                        Carica file
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-394">
                        PDF, DOC, DOCX, JPG, PNG
                      </p>
                      <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" data-id="element-395" />
                    </label>}
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-396">
                  <div className="flex items-start gap-3" data-id="element-397">
                    <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-398" />
                    <div data-id="element-399">
                      <p className="text-xs text-blue-900 leading-relaxed" data-id="element-400">
                        Le azioni certificate vengono verificate ufficialmente e
                        registrate su Blockchain. Riceverai i tuoi Punti Diamond
                        dopo la conferma.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white" data-id="element-401">
              <button onClick={handleSubmitCertification} disabled={!uploadedFile} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-402">
                Carica prova
              </button>
            </div>
          </div>
        </>}

      {/* Self-Certification Modal (Star Actions) */}
      {showSelfCertificationModal && selectedAction && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseCertificationModal} data-id="element-403" />

          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-404">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-405">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-406" />
              <div className="flex items-center justify-between" data-id="element-407">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-408">
                  Auto-certificazione
                </h3>
                <button onClick={handleCloseCertificationModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-409">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-410" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-411">
              <div className="space-y-6" data-id="element-412">
                {/* Task Info */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-413">
                  <div className="flex items-center gap-3 mb-2" data-id="element-414">
                    <div className="text-3xl" data-id="element-415">{selectedAction.emoji}</div>
                    <div className="flex-1" data-id="element-416">
                      <h4 className="font-normal text-base text-gray-900" data-id="element-417">
                        {selectedAction.title}
                      </h4>
                      <div className="flex items-center gap-1 text-xs text-gray-600 mt-1" data-id="element-418">
                        <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-419" />
                        <span data-id="element-420">+{selectedAction.points} Punti Stelle</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Photo Upload */}
                <div data-id="element-421">
                  <label className="block text-sm font-normal text-gray-900 mb-3" data-id="element-422">
                    Aggiungi una foto (opzionale)
                  </label>
                  {uploadedFile ? <div className="relative" data-id="element-423">
                      <img src={uploadedFilePreview} alt="Uploaded" className="w-full h-48 object-cover rounded-xl border border-gray-200" data-id="element-424" />
                      <button onClick={() => setUploadedFile(null)} className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors" data-id="element-425">
                        <X className="h-4 w-4 text-gray-600" data-id="element-426" />
                      </button>
                    </div> : <label className="w-full bg-gray-50 rounded-xl p-8 border-2 border-dashed border-gray-300 hover:border-green-500 transition-all cursor-pointer flex flex-col items-center justify-center" data-id="element-427">
                      <Camera className="h-12 w-12 text-gray-400 mb-3" data-id="element-428" />
                      <p className="text-sm font-normal text-gray-600 mb-1" data-id="element-429">
                        Carica una foto
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-430">
                        Documenta la tua azione
                      </p>
                      <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" data-id="element-431" />
                    </label>}
                </div>

                {/* Notes */}
                <div data-id="element-432">
                  <label className="block text-sm font-normal text-gray-900 mb-3" data-id="element-433">
                    Note aggiuntive (opzionale)
                  </label>
                  <textarea value={certificationNotes} onChange={e => setCertificationNotes(e.target.value)} placeholder="Descrivi come hai completato questa azione..." rows={4} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-green-500 transition-colors resize-none" data-id="element-434" />
                </div>

                {/* Info Box */}
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200" data-id="element-435">
                  <div className="flex items-start gap-3" data-id="element-436">
                    <FileText className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-437" />
                    <div data-id="element-438">
                      <p className="text-xs text-yellow-900 leading-relaxed" data-id="element-439">
                        L'auto-certificazione ti permette di confermare il
                        completamento dell'azione. I Punti Stelle verranno
                        aggiunti immediatamente al tuo saldo.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white" data-id="element-440">
              <button onClick={handleSubmitCertification} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-441">
                Conferma completamento
              </button>
            </div>
          </div>
        </>}
    </div>;
}